import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import analyticsService from '../../../../services/analyticsService';
import type { LearningAlertDetail, PrerequisiteInfo } from '../../../../types/radar';
import { formatLocations } from '../../../../utils/radarUtils';
import { getMockLearningAlertDetails } from '../../mockRadarData';

type UseLearningBlockContentProps = {
  annotationId: string;
  isMockMode?: boolean;
};

type UseLearningBlockContentReturn = {
  detail: LearningAlertDetail | null;
  error: string | null;
  handleToggleRollbackInfo: () => void;
  hasRollbacks: boolean;
  isLoading: boolean;
  isRollbackInfoExpanded: boolean;
  locationItems: string[];
  t: ReturnType<typeof useTranslation>['t'];
};

function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function getPrerequisiteColorClass(status: PrerequisiteInfo['status']): string {
  if (status === 'MASTERED') return 'mastered';
  return 'reviewing';
}

function getPrerequisiteStatusKey(status: PrerequisiteInfo['status']): string {
  if (status === 'MASTERED') return 'radar_detail_prereq_mastered';
  return 'radar_detail_prereq_rollback';
}

function useLearningBlockContent({ annotationId, isMockMode }: UseLearningBlockContentProps): UseLearningBlockContentReturn {
  const { i18n, t } = useTranslation(['chatbot']);
  const [detail, setDetail] = useState<LearningAlertDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRollbackInfoExpanded, setIsRollbackInfoExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!annotationId) return;

    if (isMockMode) {
      const mockDetail = getMockLearningAlertDetails(i18n.language)[annotationId] ?? null;
      setDetail(mockDetail);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDetail(): Promise<void> {
      setIsLoading(true);
      setError(null);

      try {
        const data = await analyticsService.getAnnotationDetail(annotationId);
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [annotationId, isMockMode]);

  const handleToggleRollbackInfo = useCallback(() => {
    setIsRollbackInfoExpanded((prev) => !prev);
  }, []);

  const hasRollbacks = detail ? detail.rollbackCount > 0 : false;
  const locationItems = useMemo(() => (detail ? formatLocations(detail.locations) : []), [detail]);

  return { detail, error, handleToggleRollbackInfo, hasRollbacks, isLoading, isRollbackInfoExpanded, locationItems, t };
}

export { formatPercentage, getPrerequisiteColorClass, getPrerequisiteStatusKey };
export default useLearningBlockContent;
